import { useEffect, useRef, useState } from "react";

import { useMotionValue, useScroll } from "framer-motion";

export function useVisibility(ref, offset) {
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);

  useEffect(() => {
    if (
      !visibleRef.current &&
      ref.current &&
      window.scrollY + window.innerHeight - offset >= ref.current.offsetTop
    ) {
      visibleRef.current = true;
      setVisible(true);
    }

    return scrollY.on("change", (e) => {
      if (
        !visibleRef.current &&
        ref.current &&
        e + window.innerHeight - offset >= ref.current.offsetTop
      ) {
        visibleRef.current = true;
        setVisible(true);
      }
    });
  }, [scrollY, ref, offset]);

  return visible;
}

export function useShowCTA(lastSectionVisible, rootMargin = "-20%") {
  const headerRef = useRef();
  const showingHeader = useOnScreen(headerRef, rootMargin);
  return { headerRef, showCTA: !lastSectionVisible && !showingHeader };
}

export function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          setIntersecting(lastEntry.isIntersecting);
        }
      },
      { rootMargin },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref, rootMargin]);
  return isIntersecting;
}

export const useLangDetect = () => {
  const [lang, setLang] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    setLang(window.navigator.language?.toLowerCase());

    const handleLanguageChange = () => {
      setLang(window.navigator.language?.toLowerCase());
    };

    window.addEventListener("languagechange", handleLanguageChange);

    return () => {
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);

  return lang;
};

// Replicates GSAP's pin behavior: absolute → fixed (pinned) → absolute
// Returns pinState ("before" | "pinned" | "after") and a scroll progress MotionValue [0, 1]
export function usePinScroll(placeholderRef) {
  const [pinState, setPinState] = useState("before");
  const scrollProgress = useMotionValue(0);
  const containerHeightRef = useRef(0);
  const viewportHeightRef = useRef(0);

  useEffect(() => {
    // Cache dimensions to avoid layout reflow on every scroll event
    viewportHeightRef.current = window.innerHeight;
    if (placeholderRef.current) {
      containerHeightRef.current = placeholderRef.current.offsetHeight;
    }

    const onScroll = () => {
      if (!placeholderRef.current) return;
      const rect = placeholderRef.current.getBoundingClientRect();
      const vh = viewportHeightRef.current;

      if (rect.top > 0) {
        setPinState("before");
        scrollProgress.set(0);
      } else if (rect.bottom <= vh) {
        setPinState("after");
        scrollProgress.set(1);
      } else {
        setPinState("pinned");
        const totalScroll = containerHeightRef.current - vh;
        scrollProgress.set(Math.max(0, Math.min(1, -rect.top / totalScroll)));
      }
    };

    const onResize = () => {
      viewportHeightRef.current = window.innerHeight;
      if (placeholderRef.current) {
        containerHeightRef.current = placeholderRef.current.offsetHeight;
      }
      onScroll();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { pinState, scrollProgress };
}

export function useScrollPosition() {
  const [scrollY, setScrollY] = useState(0);
  const { scrollY: motionScrollY } = useScroll();

  useEffect(() => {
    setScrollY(window.scrollY);

    return motionScrollY.on("change", (latest) => {
      setScrollY(latest);
    });
  }, [motionScrollY]);

  return scrollY;
}

export function useTidioChatVisibility(isHidden) {
  useEffect(() => {
    const updateTidioVisibility = () => {
      if (window.tidioChatApi) {
        window.tidioChatApi[isHidden ? "hide" : "show"]?.();
      }
    };

    if (typeof window !== "undefined") {
      updateTidioVisibility();
      document.addEventListener("tidioChat-ready", updateTidioVisibility);

      return () => {
        document.removeEventListener("tidioChat-ready", updateTidioVisibility);
        // Ensure the chat is shown when releasing control or on unmount
        if (window.tidioChatApi) {
          window.tidioChatApi.show?.();
        }
      };
    }
  }, [isHidden]);
}

// Moves the Tidio launcher up by `bottom` pixels while `enabled` is true.
// Tidio has no position API; `adjustStyles` is their supported way to inject
// the launcher CSS (see help.tidio.com Widget Position).
export function useTidioLauncherOffset(bottom, enabled = true) {
  useEffect(() => {
    if (typeof window === "undefined" || !enabled || !bottom) return undefined;

    let styleHash;

    const applyOffset = () => {
      if (!window.tidioChatApi?.adjustStyles) return;

      if (styleHash && window.tidioChatApi.removeStyles) {
        window.tidioChatApi.removeStyles(styleHash);
      }

      styleHash = window.tidioChatApi.adjustStyles(
        `#tidio { bottom: ${Math.round(bottom)}px !important; }`,
      );
    };

    applyOffset();
    document.addEventListener("tidioChat-ready", applyOffset);

    return () => {
      document.removeEventListener("tidioChat-ready", applyOffset);
      if (styleHash && window.tidioChatApi?.removeStyles) {
        window.tidioChatApi.removeStyles(styleHash);
      }
    };
  }, [bottom, enabled]);
}
